// src/components/admin/ProfileSettings.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  FiSave,
  FiUser,
  FiCamera,
  FiMapPin,
  FiBriefcase,
  FiCalendar,
  FiAward,
  FiStar,
  FiEdit2,
  FiX,
  FiCheck,
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiGithub,
  FiDribbble,
  FiFacebook,
  FiYoutube,
  FiExternalLink,
  FiClock,
  FiHeart,
  FiGrid,
  FiMessageCircle,
  FiHome,
  FiUsers,
  FiShoppingBag,
  FiImage,
  FiMail,
} from 'react-icons/fi';
import { FaUpwork, FaBehance } from 'react-icons/fa6';
import { BiLogoAdobe } from 'react-icons/bi';
import { TbBrandFiverr } from 'react-icons/tb';
import { SiShutterstock } from 'react-icons/si';
import { DiEnvato } from 'react-icons/di';
import { NavLink } from 'react-router-dom';
import ImageUploadPopup from './ImageUploadPopup';

function ProfileSettings() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [accountCreatedDate, setAccountCreatedDate] = useState('');
  const [showCoverPopup, setShowCoverPopup] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);

  const [userData, setUserData] = useState({
    fullName: '',
    email: '',
    username: '',
    coverImageURL: '',
    profilePictureURL: '',
    bio: '',
    longBio: '',
    location: '',
    occupation: '',
    joinDate: '',
    website: '',
    socialLinks: {
      instagram: '',
      twitter: '',
      linkedin: '',
      github: '',
      dribbble: '',
      facebook: '',
      youtube: '',
      fiverr: '',
      upwork: '',
      behance: '',
    },
    stockAccounts: {
      adobeStock: '',
      shutterstock: '',
      envato: '',
      creativeMarket: '',
    },
    professionalInfo: {
      title: '',
      company: '',
      experience: '',
      education: '',
      skills: [],
      certifications: [],
      awards: [],
    },
    stats: {
      projectsCompleted: 0,
      happyClients: 0,
      yearsExperience: 0,
      designAwards: 0,
    },
  });

  const [newSkill, setNewSkill] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newAward, setNewAward] = useState('');

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      performance
        .getEntriesByType('resource')
        .filter(entry => entry.name.startsWith('blob:'))
        .forEach(entry => URL.revokeObjectURL(entry.name));
    };
  }, []);

  // Fetch user data and account creation date
  useEffect(() => {
    fetchUserData();
    fetchAccountCreationDate();
  }, [currentUser]);

  const fetchAccountCreationDate = async () => {
    if (!currentUser?.uid) return;
    try {
      const user = currentUser;
      if (user?.metadata?.creationTime) {
        const createdDate = new Date(user.metadata.creationTime);
        setAccountCreatedDate(
          createdDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        );
      } else {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.createdAt?.toDate) {
            setAccountCreatedDate(
              data.createdAt.toDate().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
            );
          }
        }
      }
    } catch (error) {
      console.error('Error fetching account creation date:', error);
      setAccountCreatedDate('January 2024');
    }
  };

  const fetchUserData = async () => {
    if (!currentUser?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData({
          fullName: data.fullName || 'WD Hejbul',
          email: data.email || currentUser.email,
          username: data.username || '@wdhejbul',
          coverImageURL: data.coverImageURL || '',
          profilePictureURL: data.profilePictureURL || '',
          bio: data.bio || 'Creative Graphic Designer & Visual Artist',
          longBio: data.longBio || `I'm a passionate graphic designer...`,
          location: data.location || 'Dhaka, Bangladesh',
          occupation: data.occupation || 'Creative Director & Graphic Designer',
          joinDate: data.joinDate || 'January 2020',
          website: data.website || '',
          socialLinks: data.socialLinks || {
            instagram: '',
            twitter: '',
            linkedin: '',
            github: '',
            dribbble: '',
            facebook: '',
            youtube: '',
            fiverr: '',
            upwork: '',
            behance: '',
          },
          stockAccounts: data.stockAccounts || {
            adobeStock: '',
            shutterstock: '',
            envato: '',
            creativeMarket: '',
          },
          professionalInfo: data.professionalInfo || {
            title: 'Senior Graphic Designer',
            company: 'WD Hejbul Creative Studio',
            experience: '8+ years',
            education: 'Bachelor of Fine Arts',
            skills: [
              'Brand Identity',
              'Logo Design',
              'UI/UX',
              'Illustration',
              'Typography',
              'Motion Graphics',
            ],
            certifications: ['Adobe Certified Expert', 'Google UX Design'],
            awards: ['Awwwards Honorable Mention', 'CSS Design Awards'],
          },
          stats: data.stats || {
            projectsCompleted: 120,
            happyClients: 98,
            yearsExperience: 8,
            designAwards: 15,
          },
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        fullName: userData.fullName,
        username: userData.username,
        coverImageURL: userData.coverImageURL,
        profilePictureURL: userData.profilePictureURL,
        bio: userData.bio,
        longBio: userData.longBio,
        location: userData.location,
        occupation: userData.occupation,
        website: userData.website,
        socialLinks: userData.socialLinks,
        stockAccounts: userData.stockAccounts,
        professionalInfo: userData.professionalInfo,
        stats: userData.stats,
        updatedAt: serverTimestamp(),
      });
      alert('Profile updated successfully!');
      setEditingField(null);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const updateCoverImage = async imageUrl => {
    setUserData({ ...userData, coverImageURL: imageUrl });
    await handleSaveProfile();
  };

  const updateProfilePicture = async imageUrl => {
    setUserData({ ...userData, profilePictureURL: imageUrl });
    await handleSaveProfile();
  };

  const addSkill = () => {
    if (
      newSkill.trim() &&
      !userData.professionalInfo.skills.includes(newSkill.trim())
    ) {
      setUserData({
        ...userData,
        professionalInfo: {
          ...userData.professionalInfo,
          skills: [...userData.professionalInfo.skills, newSkill.trim()],
        },
      });
      setNewSkill('');
    }
  };

  const removeSkill = skillToRemove => {
    setUserData({
      ...userData,
      professionalInfo: {
        ...userData.professionalInfo,
        skills: userData.professionalInfo.skills.filter(
          skill => skill !== skillToRemove,
        ),
      },
    });
  };

  const addCertification = () => {
    if (
      newCertification.trim() &&
      !userData.professionalInfo.certifications.includes(
        newCertification.trim(),
      )
    ) {
      setUserData({
        ...userData,
        professionalInfo: {
          ...userData.professionalInfo,
          certifications: [
            ...userData.professionalInfo.certifications,
            newCertification.trim(),
          ],
        },
      });
      setNewCertification('');
    }
  };

  const removeCertification = certToRemove => {
    setUserData({
      ...userData,
      professionalInfo: {
        ...userData.professionalInfo,
        certifications: userData.professionalInfo.certifications.filter(
          cert => cert !== certToRemove,
        ),
      },
    });
  };

  const addAward = () => {
    if (
      newAward.trim() &&
      !userData.professionalInfo.awards.includes(newAward.trim())
    ) {
      setUserData({
        ...userData,
        professionalInfo: {
          ...userData.professionalInfo,
          awards: [...userData.professionalInfo.awards, newAward.trim()],
        },
      });
      setNewAward('');
    }
  };

  const removeAward = awardToRemove => {
    setUserData({
      ...userData,
      professionalInfo: {
        ...userData.professionalInfo,
        awards: userData.professionalInfo.awards.filter(
          award => award !== awardToRemove,
        ),
      },
    });
  };

  const handleViewReviews = () => {
    window.location.href = '/#testimonials';
  };

  // Navigation cards
  const internalPages = [
    {
      path: '/',
      label: 'Home',
      icon: FiHome,
      color: '#8b5cf6',
      description: 'Visit my portfolio homepage',
    },
    {
      path: '/portfolio',
      label: 'Portfolio',
      icon: FiGrid,
      color: '#ec4899',
      description: 'View my latest design projects',
    },
    {
      path: '/services',
      label: 'Services',
      icon: FiBriefcase,
      color: '#10b981',
      description: 'Explore services I offer',
    },
    {
      path: '/about',
      label: 'About',
      icon: FiUser,
      color: '#f59e0b',
      description: 'Learn more about me',
    },
    {
      path: '/contact',
      label: 'Contact',
      icon: FiMail,
      color: '#3b82f6',
      description: 'Get in touch with me',
    },
    {
      path: '/hire',
      label: 'Hire Me',
      icon: FiUsers,
      color: '#ef4444',
      description: 'Start a project with me',
    },
    {
      path: '/subscription',
      label: 'Subscription',
      icon: FiCalendar,
      color: '#a855f7',
      description: 'View subscription plans',
    },
  ];

  const socialPlatforms = [
    {
      id: 'instagram',
      label: 'Instagram',
      icon: FiInstagram,
      color: '#E4405F',
      placeholder: 'https://instagram.com/username',
    },
    {
      id: 'twitter',
      label: 'Twitter',
      icon: FiTwitter,
      color: '#1DA1F2',
      placeholder: 'https://twitter.com/username',
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      icon: FiLinkedin,
      color: '#0A66C2',
      placeholder: 'https://linkedin.com/in/username',
    },
    {
      id: 'github',
      label: 'GitHub',
      icon: FiGithub,
      color: '#333',
      placeholder: 'https://github.com/username',
    },
    {
      id: 'dribbble',
      label: 'Dribbble',
      icon: FiDribbble,
      color: '#EA4C89',
      placeholder: 'https://dribbble.com/username',
    },
    {
      id: 'behance',
      label: 'Behance',
      icon: FaBehance,
      color: '#1769FF',
      placeholder: 'https://behance.net/username',
    },
    {
      id: 'fiverr',
      label: 'Fiverr',
      icon: TbBrandFiverr,
      color: '#1DBF73',
      placeholder: 'https://fiverr.com/username',
    },
    {
      id: 'upwork',
      label: 'Upwork',
      icon: FaUpwork,
      color: '#6FDA44',
      placeholder: 'https://upwork.com/freelancers/username',
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: FiFacebook,
      color: '#1877F2',
      placeholder: 'https://facebook.com/username',
    },
    {
      id: 'youtube',
      label: 'YouTube',
      icon: FiYoutube,
      color: '#FF0000',
      placeholder: 'https://youtube.com/@username',
    },
  ];

  const stockPlatforms = [
    {
      id: 'adobeStock',
      label: 'Adobe Stock',
      icon: BiLogoAdobe,
      color: '#FF0000',
      placeholder: 'https://stock.adobe.com/contributor/username',
    },
    {
      id: 'shutterstock',
      label: 'Shutterstock',
      icon: SiShutterstock,
      color: '#FF2B2B',
      placeholder: 'https://shutterstock.com/g/username',
    },
    {
      id: 'envato',
      label: 'Envato Elements',
      icon: DiEnvato,
      color: '#82B541',
      placeholder: 'https://elements.envato.com/user/username',
    },
    {
      id: 'creativeMarket',
      label: 'Creative Market',
      icon: FiShoppingBag,
      color: '#3B9B4B',
      placeholder: 'https://creativemarket.com/users/username',
    },
  ];

  const hasCoverImage =
    userData.coverImageURL &&
    userData.coverImageURL.trim() !== '' &&
    !userData.coverImageURL.startsWith('blob:');

  return (
    <div className="flex-1">
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--bg)',
          border: '1px solid var(--border)',
        }}
      >
        {/* Cover Photo Section */}
        <div
          className="relative h-48 md:h-64 overflow-hidden"
          style={{ backgroundColor: 'var(--border)' }}
        >
          {hasCoverImage ? (
            <img
              src={userData.coverImageURL}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{ backgroundColor: 'var(--bg)' }}
            >
              <div className="absolute inset-0 opacity-5">
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle at 1px 1px, var(--text) 1px, transparent 0)',
                    backgroundSize: '40px 40px',
                  }}
                />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <h1
                    className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tighter"
                    style={{ color: 'var(--text)' }}
                  >
                    WD HEJBUL
                  </h1>
                  <div
                    className="w-20 h-px mx-auto my-4"
                    style={{ backgroundColor: 'var(--muted)' }}
                  />
                  <p
                    className="text-sm md:text-base tracking-widest uppercase"
                    style={{ color: 'var(--muted)' }}
                  >
                    Creative Design Studio
                  </p>
                </div>
              </div>
              <div className="absolute inset-0 bg-black/80 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                <div className="text-white text-center p-4 max-w-md">
                  <p className="text-sm font-mono mb-2">
                    📐 Cover Image Guidelines:
                  </p>
                  <ul className="text-xs space-y-1 font-mono">
                    <li>
                      • Recommended ratio:{' '}
                      <span className="font-bold">3:1</span> (1500x500px)
                    </li>
                    <li>
                      • Minimum width: <span className="font-bold">1200px</span>
                    </li>
                    <li>
                      • Maximum file size:{' '}
                      <span className="font-bold">5MB</span>
                    </li>
                    <li>• Formats: JPG, PNG, WebP</li>
                    <li className="text-gray-300 mt-2">
                      💡 Tip: Use high-resolution images for best results
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => setShowCoverPopup(true)}
            className="absolute bottom-4 right-4 p-2 rounded-full transition-all hover:scale-110 z-10"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <FiCamera className="w-4 h-4" style={{ color: 'var(--text)' }} />
          </button>

          {!hasCoverImage && (
            <div
              className="absolute top-4 left-4 px-2 py-1 rounded-md text-xs"
              style={{
                backgroundColor: 'var(--bg)',
                border: '1px solid var(--border)',
                color: 'var(--muted)',
              }}
            >
              Hover to see guidelines
            </div>
          )}
        </div>

        {/* Profile Picture Section */}
        <div className="relative px-6">
          <div className="absolute -top-16 left-6">
            <div className="relative group">
              <div
                className="w-32 h-32 rounded-full border-4 overflow-hidden"
                style={{
                  borderColor: 'var(--bg)',
                  backgroundColor: 'var(--border)',
                }}
              >
                {userData.profilePictureURL ? (
                  <img
                    src={userData.profilePictureURL}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FiUser
                      className="w-12 h-12"
                      style={{ color: 'var(--text)' }}
                    />
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowProfilePopup(true)}
                className="absolute bottom-1 right-1 p-1.5 rounded-full transition-all hover:scale-110 opacity-0 group-hover:opacity-100"
                style={{
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                }}
              >
                <FiCamera
                  className="w-3 h-3"
                  style={{ color: 'var(--text)' }}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Profile Info Content */}
        <div className="px-6 pt-20 pb-6">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div className="flex-1">
              {/* Name */}
              {editingField === 'name' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={userData.fullName}
                    onChange={e =>
                      setUserData({ ...userData, fullName: e.target.value })
                    }
                    className="text-2xl md:text-3xl font-bold px-3 py-1 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingField(null)}
                      className="px-3 py-1 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="px-3 py-1 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <h1
                    className="text-2xl md:text-3xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {userData.fullName}
                  </h1>
                  <button
                    onClick={() => setEditingField('name')}
                    className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <FiEdit2
                      className="w-3.5 h-3.5"
                      style={{ color: 'var(--muted)' }}
                    />
                  </button>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1"
                    style={{ backgroundColor: '#1877F2', color: 'white' }}
                  >
                    <FiCheck className="w-2.5 h-2.5" /> Verified
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Admin
                  </span>
                </div>
              )}

              {/* Username */}
              {editingField === 'username' ? (
                <div className="space-y-2 mt-1">
                  <input
                    type="text"
                    value={userData.username}
                    onChange={e =>
                      setUserData({ ...userData, username: e.target.value })
                    }
                    className="text-sm px-3 py-1 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingField(null)}
                      className="px-3 py-1 rounded-lg text-xs"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="px-3 py-1 rounded-lg text-xs"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    {userData.username}
                  </p>
                  <button
                    onClick={() => setEditingField('username')}
                    className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <FiEdit2
                      className="w-3 h-3"
                      style={{ color: 'var(--muted)' }}
                    />
                  </button>
                </div>
              )}

              {/* Location & Occupation */}
              <div
                className="flex flex-wrap items-center gap-3 mt-2 text-sm"
                style={{ color: 'var(--muted)' }}
              >
                <span className="flex items-center gap-1">
                  <FiBriefcase className="w-3.5 h-3.5" /> {userData.occupation}
                </span>
                <span className="flex items-center gap-1">
                  <FiMapPin className="w-3.5 h-3.5" /> {userData.location}
                </span>
                <span className="flex items-center gap-1">
                  <FiClock className="w-3.5 h-3.5" /> Joined{' '}
                  {accountCreatedDate}
                </span>
              </div>
            </div>

            <button
              onClick={handleSaveProfile}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
            >
              <FiSave className="w-4 h-4" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {/* Short Bio */}
          <div className="mt-4">
            {editingField === 'bio' ? (
              <div className="space-y-2">
                <textarea
                  value={userData.bio}
                  onChange={e =>
                    setUserData({ ...userData, bio: e.target.value })
                  }
                  className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                  rows="2"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingField(null)}
                    className="px-3 py-1.5 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setEditingField(null)}
                    className="px-3 py-1.5 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <p className="text-sm flex-1" style={{ color: 'var(--muted)' }}>
                  {userData.bio}
                </p>
                <button
                  onClick={() => setEditingField('bio')}
                  className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <FiEdit2
                    className="w-3.5 h-3.5"
                    style={{ color: 'var(--muted)' }}
                  />
                </button>
              </div>
            )}
          </div>

          {/* Long Bio / About */}
          <div
            className="mt-6 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <h3
              className="text-lg font-semibold mb-3"
              style={{ color: 'var(--text)' }}
            >
              About Me
            </h3>
            {editingField === 'longBio' ? (
              <div className="space-y-2">
                <textarea
                  value={userData.longBio}
                  onChange={e =>
                    setUserData({ ...userData, longBio: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl outline-none transition-all"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                  rows="8"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingField(null)}
                    className="px-3 py-1.5 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setEditingField(null)}
                    className="px-3 py-1.5 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <p
                    className="text-sm whitespace-pre-wrap"
                    style={{ color: 'var(--muted)' }}
                  >
                    {userData.longBio ||
                      'No bio added yet. Click edit to add your story.'}
                  </p>
                </div>
                <button
                  onClick={() => setEditingField('longBio')}
                  className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 flex-shrink-0"
                >
                  <FiEdit2
                    className="w-3.5 h-3.5"
                    style={{ color: 'var(--muted)' }}
                  />
                </button>
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div
              className="text-center p-3 rounded-xl"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <div className="text-xl font-bold" style={{ color: '#8b5cf6' }}>
                {userData.stats.projectsCompleted}
              </div>
              <div className="text-xs" style={{ color: 'var(--muted)' }}>
                Projects
              </div>
            </div>
            <div
              className="text-center p-3 rounded-xl"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <div className="text-xl font-bold" style={{ color: '#10b981' }}>
                {userData.stats.happyClients}%
              </div>
              <div className="text-xs" style={{ color: 'var(--muted)' }}>
                Satisfaction
              </div>
            </div>
            <div
              className="text-center p-3 rounded-xl"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <div className="text-xl font-bold" style={{ color: '#f59e0b' }}>
                {userData.stats.yearsExperience}+
              </div>
              <div className="text-xs" style={{ color: 'var(--muted)' }}>
                Experience
              </div>
            </div>
            <div
              className="text-center p-3 rounded-xl"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <div className="text-xl font-bold" style={{ color: '#ec4899' }}>
                {userData.stats.designAwards}
              </div>
              <div className="text-xs" style={{ color: 'var(--muted)' }}>
                Awards
              </div>
            </div>
          </div>

          {/* Professional Info */}
          <div
            className="mt-6 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <h2
              className="text-lg font-semibold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Professional Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label
                    className="text-xs font-medium"
                    style={{ color: 'var(--muted)' }}
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    value={userData.professionalInfo.title}
                    onChange={e =>
                      setUserData({
                        ...userData,
                        professionalInfo: {
                          ...userData.professionalInfo,
                          title: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-1.5 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
                <div>
                  <label
                    className="text-xs font-medium"
                    style={{ color: 'var(--muted)' }}
                  >
                    Company
                  </label>
                  <input
                    type="text"
                    value={userData.professionalInfo.company}
                    onChange={e =>
                      setUserData({
                        ...userData,
                        professionalInfo: {
                          ...userData.professionalInfo,
                          company: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-1.5 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
                <div>
                  <label
                    className="text-xs font-medium"
                    style={{ color: 'var(--muted)' }}
                  >
                    Experience
                  </label>
                  <input
                    type="text"
                    value={userData.professionalInfo.experience}
                    onChange={e =>
                      setUserData({
                        ...userData,
                        professionalInfo: {
                          ...userData.professionalInfo,
                          experience: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-1.5 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
                <div>
                  <label
                    className="text-xs font-medium"
                    style={{ color: 'var(--muted)' }}
                  >
                    Education
                  </label>
                  <input
                    type="text"
                    value={userData.professionalInfo.education}
                    onChange={e =>
                      setUserData({
                        ...userData,
                        professionalInfo: {
                          ...userData.professionalInfo,
                          education: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-1.5 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  className="text-xs font-medium mb-1 block"
                  style={{ color: 'var(--muted)' }}
                >
                  Skills
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {userData.professionalInfo.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                      style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                    >
                      {skill}
                      <button
                        onClick={() => removeSkill(skill)}
                        className="hover:opacity-70"
                      >
                        <FiX className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={e => setNewSkill(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && addSkill()}
                    placeholder="Add skill..."
                    className="flex-1 px-3 py-1.5 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                  <button
                    onClick={addSkill}
                    className="px-3 py-1.5 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div className="mt-4">
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Certifications
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {userData.professionalInfo.certifications.map((cert, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                    style={{ backgroundColor: '#10b981', color: 'white' }}
                  >
                    <FiAward className="w-2.5 h-2.5" /> {cert}
                    <button
                      onClick={() => removeCertification(cert)}
                      className="hover:opacity-70"
                    >
                      <FiX className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCertification}
                  onChange={e => setNewCertification(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && addCertification()}
                  placeholder="Add certification..."
                  className="flex-1 px-3 py-1.5 rounded-lg text-sm outline-none"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                />
                <button
                  onClick={addCertification}
                  className="px-3 py-1.5 rounded-lg text-sm"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Awards */}
            <div className="mt-4">
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Awards & Recognition
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {userData.professionalInfo.awards.map((award, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                    style={{ backgroundColor: '#f59e0b', color: '#1a1a1a' }}
                  >
                    <FiStar className="w-2.5 h-2.5" /> {award}
                    <button
                      onClick={() => removeAward(award)}
                      className="hover:opacity-70"
                    >
                      <FiX className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAward}
                  onChange={e => setNewAward(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && addAward()}
                  placeholder="Add award..."
                  className="flex-1 px-3 py-1.5 rounded-lg text-sm outline-none"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                />
                <button
                  onClick={addAward}
                  className="px-3 py-1.5 rounded-lg text-sm"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Cards */}
          <div
            className="mt-6 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <h2
              className="text-lg font-semibold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Quick Navigation
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {internalPages.map(page => (
                <NavLink
                  key={page.path}
                  to={page.path}
                  className="group flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${page.color}20` }}
                  >
                    <page.icon
                      className="w-5 h-5"
                      style={{ color: page.color }}
                    />
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-sm font-medium"
                      style={{ color: 'var(--text)' }}
                    >
                      {page.label}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {page.description}
                    </p>
                  </div>
                  <FiArrowRight
                    className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: 'var(--muted)' }}
                  />
                </NavLink>
              ))}
            </div>
          </div>

          {/* Reviews Button */}
          <div className="mt-4">
            <button
              onClick={handleViewReviews}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl transition-all hover:scale-[1.02]"
              style={{ backgroundColor: '#8b5cf6', color: 'white' }}
            >
              <FiMessageCircle className="w-4 h-4" /> View Client Reviews{' '}
              <FiArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Social Links */}
          <div
            className="mt-6 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <h2
              className="text-lg font-semibold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Connect & Follow
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {socialPlatforms.map(platform => {
                const url = userData.socialLinks[platform.id];
                return (
                  <div key={platform.id} className="group relative">
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02]"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <platform.icon
                            className="w-4 h-4"
                            style={{ color: platform.color }}
                          />
                        </div>
                        <div className="flex-1">
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {platform.label}
                          </p>
                          <p
                            className="text-xs truncate"
                            style={{ color: 'var(--muted)' }}
                          >
                            {url}
                          </p>
                        </div>
                        <FiExternalLink
                          className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ color: 'var(--muted)' }}
                        />
                      </a>
                    ) : (
                      <button
                        onClick={() => {
                          const newUrl = prompt(
                            `Enter your ${platform.label} profile URL:`,
                            platform.placeholder,
                          );
                          if (newUrl) {
                            setUserData({
                              ...userData,
                              socialLinks: {
                                ...userData.socialLinks,
                                [platform.id]: newUrl,
                              },
                            });
                          }
                        }}
                        className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02] w-full text-left"
                        style={{
                          backgroundColor: 'var(--border)',
                          opacity: 0.6,
                        }}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <platform.icon
                            className="w-4 h-4"
                            style={{ color: platform.color }}
                          />
                        </div>
                        <div className="flex-1">
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {platform.label}
                          </p>
                          <p
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            Click to add link
                          </p>
                        </div>
                        <FiPlus
                          className="w-3.5 h-3.5"
                          style={{ color: 'var(--muted)' }}
                        />
                      </button>
                    )}
                    {url && (
                      <button
                        onClick={() => {
                          if (confirm(`Remove ${platform.label} link?`)) {
                            setUserData({
                              ...userData,
                              socialLinks: {
                                ...userData.socialLinks,
                                [platform.id]: '',
                              },
                            });
                          }
                        }}
                        className="absolute top-2 right-2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10 dark:hover:bg-white/10"
                      >
                        <FiX
                          className="w-3 h-3"
                          style={{ color: 'var(--muted)' }}
                        />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stock Platforms */}
          <div
            className="mt-6 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <h2
              className="text-lg font-semibold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Stock & Marketplace
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {stockPlatforms.map(platform => {
                const url = userData.stockAccounts[platform.id];
                return (
                  <div key={platform.id} className="group relative">
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02]"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <platform.icon
                            className="w-4 h-4"
                            style={{ color: platform.color }}
                          />
                        </div>
                        <div className="flex-1">
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {platform.label}
                          </p>
                          <p
                            className="text-xs truncate"
                            style={{ color: 'var(--muted)' }}
                          >
                            {url}
                          </p>
                        </div>
                        <FiExternalLink
                          className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ color: 'var(--muted)' }}
                        />
                      </a>
                    ) : (
                      <button
                        onClick={() => {
                          const newUrl = prompt(
                            `Enter your ${platform.label} profile URL:`,
                            platform.placeholder,
                          );
                          if (newUrl) {
                            setUserData({
                              ...userData,
                              stockAccounts: {
                                ...userData.stockAccounts,
                                [platform.id]: newUrl,
                              },
                            });
                          }
                        }}
                        className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02] w-full text-left"
                        style={{
                          backgroundColor: 'var(--border)',
                          opacity: 0.6,
                        }}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <platform.icon
                            className="w-4 h-4"
                            style={{ color: platform.color }}
                          />
                        </div>
                        <div className="flex-1">
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {platform.label}
                          </p>
                          <p
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            Click to add link
                          </p>
                        </div>
                        <FiPlus
                          className="w-3.5 h-3.5"
                          style={{ color: 'var(--muted)' }}
                        />
                      </button>
                    )}
                    {url && (
                      <button
                        onClick={() => {
                          if (confirm(`Remove ${platform.label} link?`)) {
                            setUserData({
                              ...userData,
                              stockAccounts: {
                                ...userData.stockAccounts,
                                [platform.id]: '',
                              },
                            });
                          }
                        }}
                        className="absolute top-2 right-2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10 dark:hover:bg-white/10"
                      >
                        <FiX
                          className="w-3 h-3"
                          style={{ color: 'var(--muted)' }}
                        />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Image Upload Popups */}
      <ImageUploadPopup
        isOpen={showCoverPopup}
        onClose={() => setShowCoverPopup(false)}
        onSave={updateCoverImage}
        title="Update Cover Image"
        type="cover"
        currentImageUrl={userData.coverImageURL}
      />
      <ImageUploadPopup
        isOpen={showProfilePopup}
        onClose={() => setShowProfilePopup(false)}
        onSave={updateProfilePicture}
        title="Update Profile Picture"
        type="profile"
        currentImageUrl={userData.profilePictureURL}
      />
    </div>
  );
}

const FiPlus = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </svg>
);

const FiArrowRight = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

export default ProfileSettings;
